const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface RequestOptions extends RequestInit {
  token?: string;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getToken(): string | null {
    return localStorage.getItem('token');
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const { token, ...fetchOptions } = options;
    const authToken = token || this.getToken();

    const headers: HeadersInit = {
      ...fetchOptions.headers,
    };

    if (authToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${authToken}`;
    }

    if (fetchOptions.body && typeof fetchOptions.body === 'string') {
      (headers as Record<string, string>)['Content-Type'] = 'application/json';
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...fetchOptions,
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Request failed');
    }

    return response.json();
  }

  // Auth
  async getCurrentUser() {
    return this.request<{ user: User | null }>('/auth/me');
  }

  async logout() {
    return this.request<{ success: boolean }>('/auth/logout', { method: 'POST' });
  }

  getGoogleAuthUrl() {
    return `${this.baseUrl}/auth/google`;
  }

  // Articles
  async getArticles(params: {
    page?: number;
    limit?: number;
    tag?: string;
    search?: string;
  } = {}) {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.tag) searchParams.set('tag', params.tag);
    if (params.search) searchParams.set('search', params.search);

    const query = searchParams.toString();
    return this.request<ArticlesResponse>(`/articles${query ? `?${query}` : ''}`);
  }

  async getMyArticles(params: {
    page?: number;
    limit?: number;
    status?: string;
  } = {}) {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.status) searchParams.set('status', params.status);

    const query = searchParams.toString();
    return this.request<ArticlesResponse>(`/articles/my${query ? `?${query}` : ''}`);
  }

  async getArticle(identifier: string) {
    return this.request<{ article: Article }>(`/articles/${identifier}`);
  }

  async createArticle(data: CreateArticleData) {
    return this.request<{ article: Article }>('/articles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateArticle(id: string, data: UpdateArticleData) {
    return this.request<{ article: Article }>(`/articles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteArticle(id: string) {
    return this.request<{ success: boolean }>(`/articles/${id}`, {
      method: 'DELETE',
    });
  }

  async pinArticle(id: string, pin: boolean) {
    return this.request<{ article: Article; pinnedCount: number }>(`/articles/${id}/pin`, {
      method: 'PUT',
      body: JSON.stringify({ pin }),
    });
  }

  async getTags() {
    return this.request<{ tags: Tag[]; untaggedCount: number }>('/articles/meta/tags');
  }

  // Comments
  async getComments(articleId: string) {
    return this.request<{ comments: Comment[] }>(`/comments/article/${articleId}`);
  }

  async getAllComments(params: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
  } = {}) {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.startDate) searchParams.set('startDate', params.startDate);
    if (params.endDate) searchParams.set('endDate', params.endDate);

    const query = searchParams.toString();
    return this.request<{ comments: CommentWithArticle[]; pagination: Pagination }>(`/comments/all${query ? `?${query}` : ''}`);
  }

  async createComment(data: CreateCommentData) {
    return this.request<{ comment: Comment }>('/comments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteComment(id: string) {
    return this.request<{ success: boolean }>(`/comments/${id}`, {
      method: 'DELETE',
    });
  }

  // Upload
  async uploadImage(file: File) {
    const formData = new FormData();
    formData.append('file', file);

    const token = this.getToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}/image`, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || 'Upload failed');
    }

    return response.json() as Promise<UploadResponse>;
  }

  // Users
  async getUserStats(userId: string) {
    return this.request<{ stats: UserStats }>(`/users/${userId}/stats`);
  }

  async getDailyViewStats(userId: string, startDate?: string, endDate?: string) {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    // Pass timezone offset in minutes (negative for west of UTC)
    params.set('tzOffset', new Date().getTimezoneOffset().toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<{ dailyViews: DailyViewStat[] }>(`/users/${userId}/views/daily${query}`);
  }

  async getViewsByArticle(userId: string, startDate?: string, endDate?: string) {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    // Pass timezone offset in minutes (negative for west of UTC)
    params.set('tzOffset', new Date().getTimezoneOffset().toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<ViewsByArticleResponse>(`/users/${userId}/views/by-article${query}`);
  }
}

// Types
export interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  role: 'admin' | 'user';
  created_at: string;
}

export interface Article {
  id: string;
  title: string;
  title_zh?: string;
  slug: string;
  content: string;
  content_zh?: string;
  excerpt?: string;
  cover_image?: string;
  author_id: string;
  author_name?: string;
  author_avatar?: string;
  visibility: 'public' | 'private' | 'unlisted';
  status: 'draft' | 'published';
  tags: string;
  parsedTags?: string[];
  view_count: number;
  is_pinned?: number;
  pin_order?: number;
  pinned_at?: string;
  created_at: string;
  updated_at: string;
  published_at?: string;
}

export interface Comment {
  id: string;
  article_id: string;
  user_id?: string;
  author_name: string;
  user_name?: string;
  user_avatar?: string;
  content: string;
  parent_id?: string;
  created_at: string;
  replies?: Comment[];
}

export interface CommentWithArticle extends Comment {
  article_title?: string;
  article_slug?: string;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface Tag {
  name: string;
  count: number;
}

export interface ArticlesResponse {
  articles: Article[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CreateArticleData {
  title: string;
  title_zh?: string;
  content: string;
  content_zh?: string;
  slug?: string;
  visibility?: 'public' | 'private' | 'unlisted';
  status?: 'draft' | 'published';
  tags?: string;
}

export interface UpdateArticleData {
  title?: string;
  title_zh?: string;
  content?: string;
  content_zh?: string;
  slug?: string;
  visibility?: 'public' | 'private' | 'unlisted';
  status?: 'draft' | 'published';
  tags?: string;
}

export interface CreateCommentData {
  article_id: string;
  content: string;
  author_name?: string;
  author_email?: string;
  parent_id?: string;
}

export interface UploadResponse {
  url: string;
  key: string;
  filename: string;
  size: number;
  contentType: string;
}

export interface UserStats {
  articles: number;
  published: number;
  views: number;
  comments: number;
}

export interface DailyViewStat {
  date: string;
  views: number;
}

export interface ArticleDailyView {
  date: string;
  article_id: string;
  title: string;
  views: number;
}

export interface ViewsByArticleResponse {
  dailyData: ArticleDailyView[];
}

export const api = new ApiClient(API_BASE);
